import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle, ImageRun, PageNumber, Tab, TabStopType, TabStopPosition, ShadingType } from 'docx';
import { saveAs } from 'file-saver';
import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import imageService from './imageService';
import { decodeMermaidCode, renderMermaidToPng } from '../utils/mermaidExportHelper';

// Enhanced PDF Styles with better typography
const enhancedPdfStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 40,
    fontFamily: 'Helvetica',
    lineHeight: 1.6,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 20,
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 30,
    fontStyle: 'italic',
    color: '#666',
  },
  heading1: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 12,
    marginTop: 24,
    color: '#1a1a1a',
  },
  heading2: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 20,
    color: '#1a1a1a',
  },
  heading3: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 16,
    color: '#1a1a1a',
  },
  paragraph: {
    fontSize: 12,
    lineHeight: 1.6,
    marginBottom: 12,
    textAlign: 'justify',
    color: '#333',
  },
  boldText: {
    fontWeight: 'bold',
  },
  italicText: {
    fontStyle: 'italic',
  },
  underlineText: {
    textDecoration: 'underline',
  },
  strikeText: {
    textDecoration: 'line-through',
  },
  coloredText: {
    // Color will be set dynamically
  },
  listItem: {
    fontSize: 12,
    lineHeight: 1.6,
    marginBottom: 6,
    marginLeft: 20,
    color: '#333',
  },
  blockquote: {
    fontSize: 12,
    lineHeight: 1.6,
    marginBottom: 12,
    marginLeft: 20,
    fontStyle: 'italic',
    borderLeft: '3px solid #1976d2',
    paddingLeft: 12,
    color: '#555',
  },
  code: {
    fontSize: 10,
    fontFamily: 'Courier',
    backgroundColor: '#f5f5f5',
    padding: 4,
    borderRadius: 2,
  },
  image: {
    marginVertical: 12,
    alignSelf: 'center',
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#ddd',
    marginBottom: 12,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
  },
  tableColHeader: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    padding: 8,
  },
  tableCol: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#ddd',
    padding: 8,
  },
  tableCellHeader: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333',
  },
  tableCell: {
    fontSize: 11,
    color: '#333',
  },
});

/**
 * Body font for Word export when HTML has no inline `font-family` (matches TipTap “Default” / `.ProseMirror` in EnhancedRichTextEditor).
 * @type {string}
 */
const DEFAULT_EXPORT_FONT_FAMILY = 'Arial';

/**
 * Word “Normal” page margins: 1 inch = 1440 twips (720 twips = 0.5" / Narrow).
 * @type {number}
 */
const WORD_PAGE_MARGIN_NORMAL_TWIPS = 1440;

/**
 * Table / blockquote border stroke in eighths of a point (~1.5pt, visible but not hairline).
 * @type {number}
 */
const DOCX_TABLE_BORDER_WIDTH_NORMAL = 12;

/**
 * Heading text `size` for docx `TextRun` (half-points = 2× points). Matches TipTap / `.ProseMirror`
 * heading scale: `2em` / `1.5em` / `1.2em` with a 16px body (32px / 24px / ~19px).
 * @type {Readonly<Record<number, number>>}
 */
const HEADING_SIZE_HALF_POINTS_BY_LEVEL = Object.freeze({
  1: 48, // 24pt — 2 × 16px
  2: 36, // 18pt — 1.5 × 16px
  3: 28, // 14pt — 1.2 × 16px
});

class EnhancedExportService {
  constructor() {
    this.supportedImageFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  }

  /**
   * Enhanced Word export with full formatting and image support
   */
  async exportToWord(htmlContent, fileName, projectName = '', options = {}) {
    let elements = [];
    try {
      const normalizedHtml = this.normalizeHtmlForExport(htmlContent);

      // Process images and prepare for embedding
      const processedImages = await this.prepareImagesForWord(normalizedHtml);

      // Enhanced HTML parsing with formatting preservation
      elements = await this.parseHTMLContentEnhanced(normalizedHtml, projectName, processedImages);
      
      const sectionProperties = {
        page: {
          margin: {
            top: WORD_PAGE_MARGIN_NORMAL_TWIPS,
            right: WORD_PAGE_MARGIN_NORMAL_TWIPS,
            bottom: WORD_PAGE_MARGIN_NORMAL_TWIPS,
            left: WORD_PAGE_MARGIN_NORMAL_TWIPS,
          },
        },
      };

      const doc = new Document({
        styles: this.createWordStyles(),
        numbering: this.createNumberingConfig(),
        sections: [{
          properties: sectionProperties,
          children: elements,
        }],
      });
      

      
      const blob = await Packer.toBlob(doc);

      
      // Try alternative download method as fallback
      try {
        saveAs(blob, fileName);

      } catch (downloadError) {
        console.warn('❌ saveAs failed, trying alternative download method:', downloadError);
        
        // Alternative download method
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

      }
      
      return true;
    } catch (error) {
      console.error('Enhanced Word export failed:', error);
      console.error('HTML content length:', htmlContent?.length || 0);
      console.error('Elements parsed:', elements?.length || 0);
      
      // Fallback: try to export without images
      try {
        console.warn('Attempting fallback export without images...');
        const fallbackElements = this.createFallbackContent(this.normalizeHtmlForExport(htmlContent));
        const fallbackDoc = new Document({
          styles: this.createWordStyles(),
          sections: [{
            properties: {
              page: {
                margin: {
                  top: WORD_PAGE_MARGIN_NORMAL_TWIPS,
                  right: WORD_PAGE_MARGIN_NORMAL_TWIPS,
                  bottom: WORD_PAGE_MARGIN_NORMAL_TWIPS,
                  left: WORD_PAGE_MARGIN_NORMAL_TWIPS,
                },
              },
            },
            children: fallbackElements,
          }],
        });
        
        const blob = await Packer.toBlob(fallbackDoc);
        saveAs(blob, fileName.replace('.docx', '_fallback.docx'));
        console.warn('Fallback export completed');
        return true;
      } catch (fallbackError) {
        console.error('Fallback export also failed:', fallbackError);
        throw new Error(`Word export failed: ${error.message}. Fallback also failed: ${fallbackError.message}`);
      }
    }
  }

  /**
   * Create fallback content when main export fails (plain paragraphs only, no title banner).
   *
   * @param {string} htmlContent - Raw HTML to strip and split.
   * @returns {Array} docx `Paragraph` elements.
   */
  createFallbackContent(htmlContent) {
    const elements = [];

    // Strip HTML and create basic paragraphs
    const cleanText = htmlContent
      ?.replace(/<img[^>]*>/g, '[Image]') // Replace images with placeholder
      ?.replace(/<[^>]*>/g, '') // Remove all HTML tags
      ?.replace(/\s+/g, ' ') // Normalize whitespace
      ?.trim() || 'No content available';
    
    // Split into paragraphs
    const paragraphs = cleanText.split(/\n+/).filter(p => p.trim());
    
    paragraphs.forEach(paragraph => {
      if (paragraph.trim()) {
        elements.push(new Paragraph({
          children: [new TextRun({
            text: paragraph.trim(),
            font: DEFAULT_EXPORT_FONT_FAMILY,
            size: 22,
          })],
          spacing: { after: 200 },
        }));
      }
    });
    
    if (elements.length === 0) {
      elements.push(new Paragraph({
        children: [new TextRun({
          text: 'Document export encountered an error. Content could not be processed.',
          font: DEFAULT_EXPORT_FONT_FAMILY,
          size: 22,
        })],
      }));
    }
    
    return elements;
  }

  /**
   * Enhanced PDF export with formatting and images
   * Now opens a new tab with printable HTML so users can leverage browser "Save as PDF"
   */
  async exportToPDF(htmlContent, fileName, projectName = '', options = {}) {
    try {
      const normalizedHtml = this.normalizeHtmlForExport(htmlContent);
      // Embed images directly into the HTML so the print window has everything it needs
      const htmlWithEmbeddedImages = await this.embedImagesInHTML(normalizedHtml);
      
      // Create enhanced HTML (same styling as HTML export) to ensure fidelity
      const enhancedHTML = this.createEnhancedHTML(htmlWithEmbeddedImages, projectName, {
        ...options,
        includeDocumentHeader: false,
      });
      
      // Inject print styles + auto-print script
      const printableHTML = this.preparePrintableHTML(enhancedHTML, fileName);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Popup blocked. Please allow popups for this site to export as PDF.');
      }
      
      printWindow.document.open();
      printWindow.document.write(printableHTML);
      printWindow.document.close();
      return true;
    } catch (error) {
      console.error('Enhanced PDF export failed:', error);
      throw new Error(`PDF export failed: ${error.message}`);
    }
  }

  /**
   * Enhanced HTML export with embedded images and styles
   */
  async exportToHTML(htmlContent, fileName, projectName = '', options = {}) {
    try {
      const normalizedHtml = this.normalizeHtmlForExport(htmlContent);
      // Embed all images as base64
      const htmlWithEmbeddedImages = await this.embedImagesInHTML(normalizedHtml);
      
      // Create enhanced HTML with better styling
      const enhancedHTML = this.createEnhancedHTML(htmlWithEmbeddedImages, projectName, options);
      
      const blob = new Blob([enhancedHTML], { type: 'text/html;charset=utf-8' });
      saveAs(blob, fileName.replace('.docx', '.html'));
      return true;
    } catch (error) {
      console.error('Enhanced HTML export failed:', error);
      throw new Error(`HTML export failed: ${error.message}`);
    }
  }

  /**
   * Export requirements as a structured Word document
   */
  async exportRequirements(requirements, projectName, fileName = 'requirements.docx') {
    try {
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              children: [
                new TextRun({
                  text: `${projectName} - Requirements Document`,
                  bold: true,
                  size: 32,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 },
            }),
            
            new Paragraph({
              children: [
                new TextRun({
                  text: `Generated on ${new Date().toLocaleDateString()}`,
                  italics: true,
                  size: 22,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 800 },
            }),

            // Requirements sections
            ...this.createRequirementsSections(requirements),
          ],
        }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, fileName);
      return true;
    } catch (error) {
      console.error('Error exporting requirements:', error);
      throw new Error('Failed to export requirements to Word format');
    }
  }

  /**
   * Create structured sections for requirements
   */
  createRequirementsSections(requirements) {
    const sections = [];
    
    // Group requirements by type
    const groupedRequirements = requirements.reduce((groups, req) => {
      const type = req.type || 'General';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(req);
      return groups;
    }, {});

    // Create sections for each type
    Object.entries(groupedRequirements).forEach(([type, reqs]) => {
      // Type heading
      sections.push(
        new Paragraph({
          children: [
            new TextRun({
              text: type,
              bold: true,
              size: 26,
            }),
          ],
          heading: HeadingLevel.HEADING_1,
          spacing: {
            before: 400,
            after: 200,
          },
        })
      );

      // Requirements for this type
      reqs.forEach((req, index) => {
        // Requirement title
        sections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `${index + 1}. ${req.title}`,
                bold: true,
                size: 24,
              }),
            ],
            heading: HeadingLevel.HEADING_2,
            spacing: {
              before: 300,
              after: 100,
            },
          })
        );

        // Description
        sections.push(
          new Paragraph({
            children: [
              new TextRun({
                text: req.description,
                size: 22,
              }),
            ],
            spacing: { after: 200 },
          })
        );

        // Priority
        if (req.priority) {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Priority: ',
                  bold: true,
                  size: 22,
                }),
                new TextRun({
                  text: req.priority,
                  size: 22,
                }),
              ],
              spacing: { after: 100 },
            })
          );
        }

        // Acceptance criteria
        if (req.acceptanceCriteria && req.acceptanceCriteria.length > 0) {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Acceptance Criteria:',
                  bold: true,
                  size: 22,
                }),
              ],
              spacing: { after: 100 },
            })
          );

          req.acceptanceCriteria.forEach((criterion, criterionIndex) => {
            sections.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `  ${criterionIndex + 1}. ${criterion}`,
                    size: 22,
                  }),
                ],
                spacing: { after: 50 },
              })
            );
          });
        }

        // Add spacing after each requirement
        sections.push(
          new Paragraph({
            children: [new TextRun({ text: '' })],
            spacing: { after: 300 },
          })
        );
      });
    });

    return sections;
  }

  /**
   * Prepare images for Word export
   */
  async prepareImagesForWord(htmlContent) {
    // Use imageService's existing functionality
    const preparedImages = imageService.prepareImagesForExport(htmlContent, 'docx');
    const processedImages = new Map();

    preparedImages.forEach(img => {
      if (img.buffer) {
        // Convert ArrayBuffer to Uint8Array for docx compatibility
        const uint8Buffer = img.buffer instanceof ArrayBuffer ? new Uint8Array(img.buffer) : img.buffer;
        processedImages.set(img.src, {
          buffer: uint8Buffer,
          width: img.width || 200,
          height: img.height || 150,
          mimeType: img.mimeType,
        });
      }
    });

    return processedImages;
  }

  /**
   * Prepare images for PDF export
   */
  async prepareImagesForPDF(htmlContent) {
    const images = imageService.extractImagesFromHtml(htmlContent);
    const processedImages = new Map();

    for (const img of images) {
      if (img.src.startsWith('data:')) {
        processedImages.set(img.src, {
          src: img.src,
          width: img.width,
          height: img.height,
          alt: img.alt,
        });
      }
    }

    return processedImages;
  }

  /**
   * Enhanced HTML content parsing with formatting preservation.
   *
   * @param {string} htmlContent - Serialized editor HTML.
   * @param {string} [_projectName] - Unused; kept for call-site compatibility.
   * @param {Map} [processedImages] - Prepared image buffers by source URL.
   * @returns {Promise<Array>} docx section children.
   */
  async parseHTMLContentEnhanced(htmlContent, _projectName = '', processedImages = new Map()) {
    const elements = [];

    // Parse HTML content
    const parsedElements = await this.parseHTMLToWordElements(htmlContent, processedImages);
    elements.push(...parsedElements);

    return elements;
  }

  /**
   * Returns true if this walker node lives inside a subtree already emitted as one block
   * (`table`, `ul`/`ol`, `blockquote`). Prevents duplicate paragraphs from inner `p` / text nodes.
   *
   * @param {Node} node - DOM node from TreeWalker.
   * @returns {boolean} Whether to skip standalone handling for this node.
   */
  shouldSkipNodeHandledByAncestorBlock(node) {
    const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    if (!el || el.nodeType !== Node.ELEMENT_NODE) {
      return false;
    }
    const tag = el.tagName.toLowerCase();

    // These block-level tags are handled as whole subtrees in the switch —
    // let them through so the switch case fires, but skip their children.
    if (tag === 'table' || tag === 'ul' || tag === 'ol' || tag === 'blockquote' || tag === 'pre') {
      return false;
    }
    // The mermaid div itself must reach the switch case; only its children are skipped.
    if (tag === 'div' && el.getAttribute('data-mermaid-block') != null) {
      return false;
    }

    if (el.closest('table')) return true;
    if (el.closest('ul')) return true;
    if (el.closest('ol')) return true;
    if (el.closest('blockquote')) return true;
    if (el.closest('pre')) return true;
    if (el.closest('div[data-mermaid-block]')) return true;
    return false;
  }

  /**
   * Parse HTML to Word elements with advanced formatting
   */
  async parseHTMLToWordElements(htmlContent, processedImages) {
    const elements = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Process each element in the body
    const walker = document.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let currentParagraph = null;
    let currentRuns = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;

      if (this.shouldSkipNodeHandledByAncestorBlock(node)) {
        continue;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          const run = this.createTextRunFromNode(node, text);
          currentRuns.push(run);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        switch (tagName) {
          case 'h1':
          case 'h2':
          case 'h3':
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
            }
            elements.push(this.createHeadingFromElement(node));
            break;

          case 'p':
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
            }
            currentRuns = this.extractTextRunsFromElement(node);
            currentParagraph = this.createParagraphFromElement(node, currentRuns);
            elements.push(currentParagraph);
            currentRuns = [];
            currentParagraph = null;
            break;

          case 'img':
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
            }
            
            const imageElement = await this.createImageFromElement(node, processedImages);
            if (imageElement) {
              elements.push(imageElement);
            } else {
              // Add placeholder if image processing fails
              elements.push(new Paragraph({
                children: [new TextRun({
                  text: '[IMAGE FAILED TO LOAD]',
                  italics: true,
                  color: '888888',
                  font: DEFAULT_EXPORT_FONT_FAMILY,
                  size: 22,
                })],
                alignment: AlignmentType.CENTER,
              }));
            }
            break;

          case 'table':
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
            }
            const tableElement = this.createTableFromElement(node);
            if (tableElement) {
              elements.push(tableElement);
            }
            break;

          case 'ul':
          case 'ol':
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
            }
            const listElements = this.createListFromElement(node, tagName === 'ol');
            elements.push(...listElements);
            break;

          case 'blockquote':
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
            }
            elements.push(this.createBlockquoteFromElement(node));
            break;

          case 'hr':
            // Horizontal rules are treated as page breaks in export
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
            }
            elements.push(this.createPageBreak());
            break;

          case 'pre': {
            if (currentParagraph) {
              elements.push(this.finalizeParagraph(currentRuns));
              currentRuns = [];
              currentParagraph = null;
            }
            const codeElements = this.createCodeBlockFromElement(node);
            elements.push(...codeElements);
            break;
          }

          case 'div':
            if (node.getAttribute('data-mermaid-block') != null) {
              if (currentParagraph) {
                elements.push(this.finalizeParagraph(currentRuns));
                currentRuns = [];
                currentParagraph = null;
              }
              const mermaidImg = await this.createMermaidImageFromElement(node);
              if (mermaidImg) {
                elements.push(mermaidImg);
              }
            } else if (node.getAttribute('data-page-break') === 'true') {
              if (currentParagraph) {
                elements.push(this.finalizeParagraph(currentRuns));
                currentRuns = [];
              }
              elements.push(this.createPageBreak());
            }
            break;
          default:
            break;
        }
      }
    }

    // Finalize any remaining paragraph
    if (currentRuns.length > 0) {
      elements.push(this.finalizeParagraph(currentRuns));
    }

    return elements;
  }

  /**
   * Create text run with formatting from DOM node.
   *
   * @param {Text} textNode - DOM text node.
   * @param {string} text - Literal segment text.
   * @param {object} [runOverrides] - Extra TextRun fields (e.g. `{ bold: true }` for `<th>` cells).
   * @param {number | null} [defaultSizeHalfPoints] - Used when no `font-size` in ancestor chain (e.g. headings).
   */
  createTextRunFromNode(textNode, text, runOverrides = {}, defaultSizeHalfPoints = null) {
    const parent = textNode.parentElement;
    const formatting = this.extractFormattingFromElement(parent);
    let size = formatting.size;
    if (defaultSizeHalfPoints != null && !this.hasFontSizeInAncestors(parent)) {
      size = defaultSizeHalfPoints;
    }

    return new TextRun({
      text,
      ...formatting,
      size,
      ...runOverrides,
    });
  }

  /**
   * @param {Element | null} element - Starting element (e.g. parent of a text node).
   * @returns {boolean} Whether any ancestor sets `font-size`.
   */
  hasFontSizeInAncestors(element) {
    let el = element;
    while (el && el.nodeType === Node.ELEMENT_NODE) {
      const st = el.style;
      if (st && st.fontSize) {
        return true;
      }
      const raw = el.getAttribute && el.getAttribute('style');
      if (raw && /font-size\s*:/i.test(raw)) {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  /**
   * Extract formatting from DOM element
   */
  extractFormattingFromElement(element) {
    const formatting = {
      size: 22, // Default size
    };

    // Walk up the DOM tree to collect all formatting
    let current = element;
    while (current && current.tagName) {
      const tagName = current.tagName.toLowerCase();
      const style = current.style || {};

      // Tag-based formatting
      switch (tagName) {
        case 'strong':
        case 'b':
          formatting.bold = true;
          break;
        case 'em':
        case 'i':
          formatting.italics = true;
          break;
        case 'u':
          formatting.underline = {};
          break;
        case 'strike':
        case 's':
          formatting.strike = true;
          break;
        case 'sub':
          formatting.subScript = true;
          break;
        case 'sup':
          formatting.superScript = true;
          break;
        case 'code':
          formatting.font = 'Courier New';
          formatting.size = 20;
          break;
        default:
          break;
      }

      // Style-based formatting
      if (style.fontWeight === 'bold' || style.fontWeight >= '600') {
        formatting.bold = true;
      }
      if (style.fontStyle === 'italic') {
        formatting.italics = true;
      }
      if (style.textDecoration?.includes('underline')) {
        formatting.underline = {};
      }
      if (style.textDecoration?.includes('line-through')) {
        formatting.strike = true;
      }
      if (style.color) {
        formatting.color = this.convertColorToHex(style.color);
      }
      if (style.backgroundColor && style.backgroundColor !== 'transparent') {
        formatting.highlight = this.convertColorToHex(style.backgroundColor);
      }
      if (style.fontSize) {
        const fontSize = this.convertFontSizeToPoints(style.fontSize);
        if (fontSize) {
          formatting.size = fontSize;
        }
      }
      if (style.fontFamily) {
        formatting.font = style.fontFamily.replace(/['"]/g, '').split(',')[0];
      }

      // Fallback: parse raw style attribute (some parsers expose empty .style)
      this.mergeFontFromStyleAttribute(current, formatting);

      current = current.parentElement;
    }

    if (!formatting.font) {
      formatting.font = DEFAULT_EXPORT_FONT_FAMILY;
    }

    return formatting;
  }

  /**
   * Merges `font-family` / `font-size` from the element's raw `style` attribute when missing.
   *
   * @param {Element} element - DOM element.
   * @param {object} formatting - Mutable formatting object for TextRun.
   */
  mergeFontFromStyleAttribute(element, formatting) {
    const raw = element.getAttribute && element.getAttribute('style');
    if (!raw) return;
    const style = element.style || {};

    if (!formatting.font && !style.fontFamily) {
      const ff = raw.match(/font-family\s*:\s*([^;]+)/i);
      if (ff) {
        formatting.font = ff[1].replace(/['"]/g, '').split(',')[0].trim();
      }
    }
    if (!style.fontSize) {
      const fs = raw.match(/font-size\s*:\s*([^;]+)/i);
      if (fs) {
        const pts = this.convertFontSizeToPoints(fs[1].trim());
        if (pts) {
          formatting.size = pts;
        }
      }
    }
  }

  /**
   * Create heading from DOM element
   */
  createHeadingFromElement(element) {
    const level = parseInt(element.tagName.charAt(1), 10);
    const alignment = this.getAlignmentFromElement(element);

    const headingLevel = level === 1 ? HeadingLevel.HEADING_1 :
                        level === 2 ? HeadingLevel.HEADING_2 :
                        HeadingLevel.HEADING_3;

    const defaultHeadingSize =
      HEADING_SIZE_HALF_POINTS_BY_LEVEL[level] ?? HEADING_SIZE_HALF_POINTS_BY_LEVEL[3];

    const runs = this.extractTextRunsFromElement(
      element,
      { bold: true, color: '1a1a1a' },
      defaultHeadingSize
    );

    return new Paragraph({
      children: runs.length > 0 ? runs : [
        new TextRun({
          text: element.textContent.trim(),
          bold: true,
          size: defaultHeadingSize,
          color: '1a1a1a',
          font: DEFAULT_EXPORT_FONT_FAMILY,
        }),
      ],
      heading: headingLevel,
      alignment: alignment,
      spacing: {
        before: 400,
        after: 200,
      },
    });
  }

  /**
   * Create paragraph from DOM element
   */
  createParagraphFromElement(element, textRuns) {
    const alignment = this.getAlignmentFromElement(element);
    const spacing = this.getSpacingFromElement(element);

    return new Paragraph({
      children: textRuns,
      alignment: alignment,
      spacing: spacing,
    });
  }

  /**
   * Extract text runs from DOM element (preserves inline font/color from TipTap `span[style]`).
   *
   * @param {Element} element - Root element to walk (e.g. `<p>` or cloned `<li>`).
   * @param {object} [runOverrides] - Applied to every run (e.g. `{ bold: true }` for `<th>` cells).
   * @param {number | null} [defaultSizeHalfPoints] - Default docx half-point size when DOM has no font-size.
   * @returns {Array} docx TextRun instances.
   */
  extractTextRunsFromElement(element, runOverrides = {}, defaultSizeHalfPoints = null) {
    const runs = [];
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    while (walker.nextNode()) {
      const textNode = walker.currentNode;
      const text = textNode.textContent;
      if (text.length === 0) continue;
      runs.push(this.createTextRunFromNode(textNode, text, runOverrides, defaultSizeHalfPoints));
    }

    return runs;
  }

  /**
   * Create image from DOM element
   */
  async createImageFromElement(element, processedImages) {
    const src = element.src;

    
    // Handle direct Base64 data URLs (new image aliasing system)
    if (src.startsWith('data:image/')) {

      return await this.createImageFromDataURL(src, element);
    }
    
    // Handle processed images (legacy system)
    const imageData = processedImages.get(src);
    
    if (!imageData) {
      console.warn('Image not found in processed images:', src);
      return null;
    }

    try {
      // Calculate dimensions (max 600px width to fit in document)
      const maxWidth = 600;
      const originalWidth = imageData.width || 300;
      const originalHeight = imageData.height || 200;
      
      let width = originalWidth;
      let height = originalHeight;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      const imageRun = new ImageRun({
        data: imageData.buffer,
        transformation: {
          width: Math.round(width * 9525), // Convert pixels to EMU
          height: Math.round(height * 9525),
        },
      });

      return new Paragraph({
        children: [imageRun],
        alignment: this.getImageAlignmentFromElement(element),
        spacing: { before: 200, after: 200 },
      });
    } catch (error) {
      console.error('Failed to create image element:', error);
      return null;
    }
  }

  /**
   * Create image from Base64 data URL (new image aliasing system)
   */
  async createImageFromDataURL(dataUrl, element) {
    try {
      // Validate data URL format
      if (!dataUrl || !dataUrl.startsWith('data:image/')) {
        console.error('❌ Invalid data URL format:', dataUrl?.substring(0, 50) + '...');
        return this.createPlaceholderParagraph('Image unavailable - invalid format');
      }

      // Extract Base64 data
      const base64Data = dataUrl.split(',')[1];
      if (!base64Data || base64Data.length < 100) {
        console.error('❌ Invalid or truncated Base64 data:', base64Data?.length || 0, 'characters');
        return this.createPlaceholderParagraph('Image unavailable - truncated data');
      }

      // Use imageService to convert Base64 to buffer
      let buffer;
      try {
        const bufferResult = imageService.base64ToBuffer(base64Data);
        if (!bufferResult) {
          console.error('❌ ImageService failed to convert base64 to buffer');
          return this.createPlaceholderParagraph('Image unavailable - conversion failed');
        }
        // Convert ArrayBuffer to Uint8Array for docx compatibility
        buffer = bufferResult instanceof ArrayBuffer ? new Uint8Array(bufferResult) : bufferResult;
      } catch (decodeError) {
        console.error('❌ Base64 decode failed:', decodeError.message);
        return this.createPlaceholderParagraph('Image unavailable - decode error');
      }

      // Validate buffer size
      if (buffer.length < 1000) {
        console.error('❌ Image buffer too small:', buffer.length, 'bytes');
        return this.createPlaceholderParagraph('Image unavailable - invalid size');
      }

      // Use conservative dimensions for reliable Word document compatibility
      const maxWidth = 600;
      let width =
        parseInt(element.getAttribute('width'), 10) ||
        this.parseCssPx(element.style && element.style.width) ||
        200;
      let height =
        parseInt(element.getAttribute('height'), 10) ||
        this.parseCssPx(element.style && element.style.height) ||
        150;

      // Scale down if too wide
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      // Ensure reasonable dimensions (not too small or too large)
      width = Math.max(Math.min(width, maxWidth), 50);
      height = Math.max(Math.min(height, 450), 40);
      
      let imageRun;
      try {
        // Use pixel-based dimensions (proven working approach)
        imageRun = new ImageRun({
          data: buffer,
          transformation: {
            width: width,
            height: height,
          }
        });

      } catch (imageRunError) {
        console.error('❌ Image creation failed:', imageRunError.message);
        return this.createPlaceholderParagraph('Image unavailable - creation failed');
      }

      const paragraph = new Paragraph({
        children: [imageRun],
        alignment: this.getImageAlignmentFromElement(element),
        spacing: { before: 200, after: 200 },
      });
      

      return paragraph;
    } catch (error) {
      console.error('❌ Failed to create image from data URL:', error);
      console.error('❌ Error stack:', error.stack);
      return this.createPlaceholderParagraph(`Image unavailable - ${error.message}`);
    }
  }

  /**
   * Create a placeholder paragraph for failed images
   */
  createPlaceholderParagraph(message) {
    return new Paragraph({
      children: [
        new TextRun({
          text: `[${message}]`,
          italics: true,
          color: '888888',
          font: DEFAULT_EXPORT_FONT_FAMILY,
          size: 22,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 200 },
    });
  }

  /**
   * Create table from DOM element
   */
  createTableFromElement(element) {
    const rows = element.querySelectorAll('tr');
    if (rows.length === 0) return null;

    const tableRows = Array.from(rows).map(row => {
      const cells = row.querySelectorAll('td, th');
      const tableCells = Array.from(cells).map(cell => {
        const isHeader = cell.tagName.toLowerCase() === 'th';
        const cellWidth = this.getTableCellWidthFromElement(cell);
        const runs = this.extractTextRunsFromElement(cell, isHeader ? { bold: true } : {});
        const cellParagraphs =
          runs.length > 0
            ? [new Paragraph({ children: runs })]
            : [new Paragraph({
              children: [new TextRun({
                text: cell.textContent.trim(),
                size: 22,
                font: DEFAULT_EXPORT_FONT_FAMILY,
              })],
            })];

        return new TableCell({
          children: cellParagraphs,
          ...(cellWidth ? { width: cellWidth } : {}),
          borders: {
            top: { style: BorderStyle.SINGLE, size: DOCX_TABLE_BORDER_WIDTH_NORMAL, color: 'dddddd' },
            bottom: { style: BorderStyle.SINGLE, size: DOCX_TABLE_BORDER_WIDTH_NORMAL, color: 'dddddd' },
            left: { style: BorderStyle.SINGLE, size: DOCX_TABLE_BORDER_WIDTH_NORMAL, color: 'dddddd' },
            right: { style: BorderStyle.SINGLE, size: DOCX_TABLE_BORDER_WIDTH_NORMAL, color: 'dddddd' },
          },
          shading: isHeader ? { fill: 'f5f5f5' } : undefined,
        });
      });

      return new TableRow({ children: tableCells });
    });

    return new Table({
      rows: tableRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
    });
  }

  /**
   * Create list from DOM element with nested indentation support
   */
  createListFromElement(element, isOrdered = false) {
    const items = [];
    
    // Process all list items recursively to handle nesting
    this.processListItems(element, items, isOrdered, 0);
    
    return items;
  }

  /**
   * Recursively process list items to handle nesting levels
   */
  processListItems(container, items, isOrdered, level) {
    const directChildren = Array.from(container.children);
    
    for (const child of directChildren) {
      if (child.tagName.toLowerCase() === 'li') {
        const liClone = child.cloneNode(true);
        liClone.querySelectorAll('ul, ol').forEach((n) => n.remove());
        const runs = this.extractTextRunsFromElement(liClone);

        if (runs.length > 0) {
          items.push(new Paragraph({
            children: runs,
            bullet: isOrdered ? undefined : { level: level },
            numbering: isOrdered ? { reference: 'default-numbering', level: level } : undefined,
            spacing: { after: 100 },
          }));
        }
        
        // Check for nested lists within this li
        const nestedLists = child.querySelectorAll(':scope > ul, :scope > ol');
        nestedLists.forEach(nestedList => {
          const nestedIsOrdered = nestedList.tagName.toLowerCase() === 'ol';
          this.processListItems(nestedList, items, nestedIsOrdered, level + 1);
        });
      }
    }
  }

  /**
   * Create blockquote from DOM element
   */
  createBlockquoteFromElement(element) {
    const runs = this.extractTextRunsFromElement(element, { italics: true, color: '555555' });

    return new Paragraph({
      children:
        runs.length > 0
          ? runs
          : [
              new TextRun({
                text: element.textContent.trim(),
                italics: true,
                size: 22,
                color: '555555',
                font: DEFAULT_EXPORT_FONT_FAMILY,
              }),
            ],
      indent: { left: 720 }, // 0.5 inch
      spacing: { before: 200, after: 200 },
      border: {
        left: {
          color: '1976d2',
          size: DOCX_TABLE_BORDER_WIDTH_NORMAL,
          style: BorderStyle.SINGLE,
        },
      },
    });
  }

  /**
   * Create Word paragraphs from a `<pre>` code block element.
   * Each line becomes a separate paragraph with a monospace font and a light
   * grey background shading to visually distinguish it from normal text.
   *
   * @param {Element} element - The `<pre>` DOM element.
   * @returns {Paragraph[]} Array of docx Paragraph instances.
   */
  createCodeBlockFromElement(element) {
    const text = element.textContent || '';
    const lines = text.split('\n');
    const codeShading = {
      type: ShadingType.CLEAR,
      fill: 'f5f5f5',
      color: 'auto',
    };

    return lines.map((line, idx) => new Paragraph({
      children: [
        new TextRun({
          text: line || ' ',
          font: 'Courier New',
          size: 18,
          color: '333333',
        }),
      ],
      spacing: {
        before: idx === 0 ? 200 : 0,
        after: idx === lines.length - 1 ? 200 : 0,
        line: 276,
      },
      shading: codeShading,
      indent: { left: 360 },
    }));
  }

  /**
   * Render a `<div data-mermaid-block>` element to an SVG, convert it to a
   * PNG via a canvas, and return a docx Paragraph containing an ImageRun.
   *
   * Falls back to a monospace code block if rendering fails.
   *
   * @param {Element} element - The `<div data-mermaid-block>` DOM element.
   * @returns {Promise<Paragraph|null>} A paragraph with the diagram image.
   */
  async createMermaidImageFromElement(element) {
    const rawCode = element.getAttribute('data-mermaid-code') || element.textContent || '';
    if (!rawCode.trim()) return null;

    const code = decodeMermaidCode(rawCode);

    try {
      const pngBuffer = await renderMermaidToPng(code);
      if (!pngBuffer) {
        return this.createMermaidFallbackCodeBlock(code);
      }

      return new Paragraph({
        children: [
          new ImageRun({
            data: pngBuffer.buffer,
            transformation: { width: pngBuffer.width, height: pngBuffer.height },
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 200 },
      });
    } catch (err) {
      console.warn('Mermaid export render failed, using code fallback:', err);
      return this.createMermaidFallbackCodeBlock(code);
    }
  }

  /**
   * Fallback: render mermaid source as a styled code block when SVG→PNG fails.
   *
   * @param {string} code - Mermaid source text.
   * @returns {Paragraph} A monospace paragraph with the raw code.
   */
  createMermaidFallbackCodeBlock(code) {
    const lines = code.trim().split('\n');
    const codeShading = { type: ShadingType.CLEAR, fill: 'f5f5f5', color: 'auto' };

    const children = [];
    lines.forEach((line, idx) => {
      if (idx > 0) children.push(new TextRun({ break: 1 }));
      children.push(new TextRun({
        text: line || ' ',
        font: 'Courier New',
        size: 18,
        color: '333333',
      }));
    });

    return new Paragraph({
      children,
      spacing: { before: 200, after: 200, line: 276 },
      shading: codeShading,
      indent: { left: 360 },
    });
  }

  /**
   * Create page break element
   */
  createPageBreak() {
    return new Paragraph({
      children: [new TextRun({
        text: '',
        font: DEFAULT_EXPORT_FONT_FAMILY,
        size: 22,
      })],
      pageBreakBefore: true,
    });
  }

  /**
   * Extract header/footer data from HTML metadata comments
   */
  extractHeaderFooterFromHTML(htmlContent) {
    try {
      const match = htmlContent.match(/<!--HEADER_FOOTER_START-->\s*([\s\S]*?)\s*<!--HEADER_FOOTER_END-->/);
      if (match && match[1]) {
        let jsonString = match[1].trim();
        
        // Clean up any potential formatting issues
        jsonString = jsonString.replace(/\n/g, '').replace(/\r/g, '');
        
        // Validate that we have a complete JSON object
        if (!jsonString.startsWith('{') || !jsonString.endsWith('}')) {
          console.warn('Export Service: Invalid JSON structure in header/footer metadata');
          return null;
        }
        
        const parsed = JSON.parse(jsonString);
        
        // Validate the structure
        if (typeof parsed === 'object' && parsed.hasOwnProperty('showHeaderFooter')) {
          return parsed;
        } else {
          console.warn('Export Service: Invalid header/footer metadata structure');
          return null;
        }
      }
    } catch (error) {
      console.warn('Export Service: Failed to parse header/footer metadata:', error);
    }
    return null;
  }

  /**
   * Remove header/footer metadata from HTML content
   */
  cleanHtmlFromMetadata(htmlContent) {
    return htmlContent.replace(/<!--HEADER_FOOTER_START-->[\s\S]*?<!--HEADER_FOOTER_END-->\s*/g, '');
  }

  /**
   * Removes AI diff-review spans (accept-all semantics): strips deletions and unwraps insertions.
   *
   * @param {string} htmlContent - Raw HTML string.
   * @returns {string} HTML safe for export.
   */
  stripDiffReviewMarkup(htmlContent) {
    if (!htmlContent || typeof htmlContent !== 'string') {
      return '';
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    doc.querySelectorAll('span[data-diff-delete]').forEach((span) => {
      span.remove();
    });

    doc.querySelectorAll('span[data-diff-insert]').forEach((span) => {
      const fragment = doc.createDocumentFragment();
      while (span.firstChild) {
        fragment.appendChild(span.firstChild);
      }
      if (span.parentNode) {
        span.parentNode.replaceChild(fragment, span);
      }
    });

    return doc.body.innerHTML;
  }

  /**
   * Normalizes editor HTML for export: legacy metadata cleanup and diff-review stripping.
   *
   * @param {string} htmlContent - Editor HTML.
   * @returns {string} HTML for Word/PDF/HTML export pipelines.
   */
  normalizeHtmlForExport(htmlContent) {
    let html = this.cleanHtmlFromMetadata(htmlContent || '');
    html = this.stripDiffReviewMarkup(html);
    return html;
  }

  /**
   * Maps `data-align` on an `<img>` to docx alignment.
   *
   * @param {Element} element - Image DOM node.
   * @returns {typeof AlignmentType[keyof typeof AlignmentType]} Alignment enum value.
   */
  getImageAlignmentFromElement(element) {
    const align = (element && element.getAttribute('data-align')) || 'center';
    switch (align) {
      case 'left':
        return AlignmentType.LEFT;
      case 'right':
        return AlignmentType.RIGHT;
      default:
        return AlignmentType.CENTER;
    }
  }

  /**
   * Reads per-cell width hints from TipTap/table DOM for docx `TableCell`.
   * Percent widths use docx convention: size = percent × 50 (e.g. 50% → 2500).
   *
   * @param {Element} cell - `td` or `th` element.
   * @returns {{ size: number, type: typeof WidthType[keyof typeof WidthType] } | undefined} Width spec or undefined for auto.
   */
  getTableCellWidthFromElement(cell) {
    if (!cell) return undefined;

    const styleWidth = (cell.style && cell.style.width) || '';
    const pctMatch = styleWidth.match(/^([\d.]+)%\s*$/);
    if (pctMatch) {
      const n = parseFloat(pctMatch[1]);
      if (!Number.isNaN(n) && n > 0) {
        const pct = Math.min(100, n);
        return { size: Math.round(pct * 50), type: WidthType.PERCENTAGE };
      }
    }

    const pxMatch = styleWidth.match(/^([\d.]+)px\s*$/i);
    if (pxMatch) {
      const px = parseFloat(pxMatch[1]);
      if (!Number.isNaN(px) && px > 0) {
        const twips = Math.round(px * 15);
        return { size: twips, type: WidthType.DXA };
      }
    }

    return undefined;
  }

  /**
   * Parses a CSS length like `120px` to an integer pixel value.
   *
   * @param {string} value - CSS length or empty.
   * @returns {number | undefined} Pixel number if parseable.
   */
  parseCssPx(value) {
    if (!value || typeof value !== 'string') return undefined;
    const m = value.trim().match(/^([\d.]+)px$/i);
    if (!m) return undefined;
    const n = parseFloat(m[1]);
    return Number.isNaN(n) ? undefined : Math.round(n);
  }

  /**
   * Utility functions
   */
  getAlignmentFromElement(element) {
    const textAlign = element.style.textAlign;
    switch (textAlign) {
      case 'center': return AlignmentType.CENTER;
      case 'right': return AlignmentType.RIGHT;
      case 'justify': return AlignmentType.JUSTIFIED;
      default: return AlignmentType.LEFT;
    }
  }

  getSpacingFromElement(element) {
    const lineHeight = element.style.lineHeight;
    const marginTop = element.style.marginTop;
    const marginBottom = element.style.marginBottom;

    return {
      before: marginTop ? this.convertSizeToTwips(marginTop) : 120,
      after: marginBottom ? this.convertSizeToTwips(marginBottom) : 120,
      line: lineHeight ? Math.round(parseFloat(lineHeight) * 240) : 276, // 1.15 line spacing
    };
  }

  convertColorToHex(color) {
    // Convert various color formats to hex without #
    if (color.startsWith('#')) {
      return color.substring(1);
    }
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        return matches.slice(0, 3)
          .map(n => parseInt(n).toString(16).padStart(2, '0'))
          .join('');
      }
    }
    // Color name to hex mapping (simplified)
    const colorMap = {
      'black': '000000',
      'white': 'ffffff',
      'red': 'ff0000',
      'green': '00ff00',
      'blue': '0000ff',
      'yellow': 'ffff00',
      'cyan': '00ffff',
      'magenta': 'ff00ff',
    };
    return colorMap[color.toLowerCase()] || '000000';
  }

  convertFontSizeToPoints(fontSize) {
    if (fontSize.endsWith('px')) {
      return Math.round(parseFloat(fontSize) * 0.75 * 2); // Convert px to half-points
    }
    if (fontSize.endsWith('pt')) {
      return Math.round(parseFloat(fontSize) * 2); // Convert points to half-points
    }
    if (fontSize.endsWith('em')) {
      return Math.round(parseFloat(fontSize) * 16 * 0.75 * 2); // Assume 16px base
    }
    return null;
  }

  convertSizeToTwips(size) {
    if (size.endsWith('px')) {
      return Math.round(parseFloat(size) * 15); // Convert px to twips
    }
    if (size.endsWith('pt')) {
      return Math.round(parseFloat(size) * 20); // Convert points to twips
    }
    return 120; // Default
  }

  finalizeParagraph(textRuns) {
    if (textRuns.length === 0) {
      return new Paragraph({
        children: [new TextRun({
          text: '',
          size: 22,
          font: DEFAULT_EXPORT_FONT_FAMILY,
        })],
      });
    }
    
    return new Paragraph({
      children: textRuns,
      spacing: { after: 120 },
    });
  }

  extractImageId(dataUrl) {
    // Extract image ID from data URL or return null
    const match = dataUrl.match(/data-image-id="([^"]+)"/);
    return match ? match[1] : null;
  }

  /**
   * Create header/footer content with image support
   */
  async createHeaderFooterContent(htmlContent, settings, isHeader) {
    try {
      // Parse HTML content to extract text and images
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      const alignment = this.getAlignmentFromElement(doc.body);
      
      // Process all child nodes
      const textRuns = [];
      const images = [];
      
      await this.processHeaderFooterNodes(doc.body, textRuns, images);
      
      // Add page number if enabled
      if (settings.includePageNumber) {
        const pageNumberText = this.createPageNumberText(settings);
        
        if (settings.pageNumberPosition === 'left') {
          textRuns.unshift(...pageNumberText);
        } else if (settings.pageNumberPosition === 'center') {
          textRuns.push(new Tab());
          textRuns.push(...pageNumberText);
        } else { // right
          textRuns.push(new Tab());
          textRuns.push(...pageNumberText);
        }
      }
      
      // For headers/footers, create separate paragraphs for images to prevent corruption
      // This is safer than mixing text and images in a single paragraph
      
      const tabs = settings.includePageNumber ? this.createTabsForPosition(settings.pageNumberPosition) : [];
      
      // If we only have text (no images), return a simple paragraph
      if (images.length === 0) {
        return new Paragraph({
          children: textRuns.length > 0 ? textRuns : [new TextRun('')],
          alignment: alignment,
          tabStops: tabs.length > 0 ? tabs : undefined,
        });
      }
      
      // If we have images, create them with very conservative settings
      // Use only the first image to prevent complexity issues
      if (images.length > 0) {
        try {
          const image = images[0]; // Use only the first image for safety
          
          // Validate image buffer
          if (image.buffer && image.buffer.length >= 100) {
            // Use very small, safe dimensions for header/footer images
            const width = 40; // Fixed small size
            const height = 30; // Fixed small size
            
            const imageRun = new ImageRun({
              data: image.buffer,
              transformation: {
                width: width,
                height: height,
              },
            });
            
            // Create a simple paragraph with just the image
            return new Paragraph({
              children: [imageRun],
              alignment: AlignmentType.LEFT,
            });
          }
        } catch (imageError) {
          console.error('Failed to create header/footer image, falling back to text:', imageError);
        }
      }
      
      // Fallback to text-only paragraph
      return new Paragraph({
        children: textRuns.length > 0 ? textRuns : [new TextRun('')],
        alignment: alignment,
        tabStops: tabs.length > 0 ? tabs : undefined,
      });
      
    } catch (error) {
      console.error('Error creating header/footer content:', error);
      // Fallback to simple text
      return new Paragraph({
        children: [new TextRun(htmlContent.replace(/<[^>]*>/g, ''))],
        alignment: AlignmentType.CENTER,
      });
    }
  }

  /**
   * Process nodes in header/footer content
   */
  async processHeaderFooterNodes(node, textRuns, images) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent.trim()) {
        textRuns.push(new TextRun({
          text: node.textContent,
          size: 20, // 10pt for headers/footers
        }));
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      
      if (tagName === 'img') {
        const src = node.getAttribute('src');
        const alt = node.getAttribute('alt') || '';
        
        if (src && src.startsWith('data:')) {
          try {
            // Use imageService to extract base64 data and mimeType
            const matches = src.match(/^data:([^;]+);base64,(.+)$/);
            if (matches) {
              const mimeType = matches[1];
              const base64 = matches[2];
              const buffer = imageService.base64ToBuffer(base64);
              
              if (buffer) {
                // Convert ArrayBuffer to Uint8Array for docx compatibility
                const uint8Buffer = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer;
                
                // Get dimensions from the node, with safer defaults for headers/footers
                let width = parseInt(node.getAttribute('width')) || 60;
                let height = parseInt(node.getAttribute('height')) || 40;
                
                // Additional safety check for header/footer images
                if (height === 'auto' || isNaN(height)) {
                  height = Math.round(width * 0.75); // Maintain reasonable aspect ratio
                }
                
                images.push({
                  buffer: uint8Buffer,
                  mimeType: mimeType,
                  alt: alt,
                  width: width,
                  height: height,
                });
              }
            }
          } catch (error) {
            console.error('Error processing header/footer image:', error);
          }
        }
      } else if (tagName === 'strong' || tagName === 'b') {
        const childTextRuns = [];
        await this.processHeaderFooterNodes(node, childTextRuns, images);
        childTextRuns.forEach(run => {
          if (run instanceof TextRun) {
            run.bold = true;
          }
        });
        textRuns.push(...childTextRuns);
      } else if (tagName === 'em' || tagName === 'i') {
        const childTextRuns = [];
        await this.processHeaderFooterNodes(node, childTextRuns, images);
        childTextRuns.forEach(run => {
          if (run instanceof TextRun) {
            run.italics = true;
          }
        });
        textRuns.push(...childTextRuns);
      } else if (tagName === 'u') {
        const childTextRuns = [];
        await this.processHeaderFooterNodes(node, childTextRuns, images);
        childTextRuns.forEach(run => {
          if (run instanceof TextRun) {
            run.underline = {};
          }
        });
        textRuns.push(...childTextRuns);
      } else {
        // Process child nodes for other elements
        for (const child of node.childNodes) {
          await this.processHeaderFooterNodes(child, textRuns, images);
        }
      }
    }
  }

  // Removed processBase64Image - now using imageService.base64ToBuffer() instead

  /**
   * Create image paragraphs for headers/footers (legacy - now integrated into main paragraph)
   */
  async createImageParagraphs(images) {
    // This function is kept for backward compatibility but is no longer used
    // Images are now integrated directly into the main paragraph
    const paragraphs = [];
    
    for (const image of images) {
      try {
        const imageRun = new ImageRun({
          data: image.buffer,
          transformation: {
            width: Math.min(image.width || 50, 100),
            height: image.height === 'auto' ? undefined : Math.min(image.height || 50, 100),
          },
        });
        
        paragraphs.push(new Paragraph({
          children: [imageRun],
          alignment: AlignmentType.CENTER,
        }));
      } catch (error) {
        console.error('Error creating header/footer image:', error);
      }
    }
    
    return paragraphs;
  }

  /**
   * Create page number text runs
   */
  createPageNumberText(settings) {
    const format = settings.pageNumberFormat || 'Page {page} of {total}';
    const parts = format.split(/(\{page\}|\{total\})/);
    const textRuns = [];
    
    parts.forEach(part => {
      if (part === '{page}') {
        textRuns.push(PageNumber.CURRENT);
      } else if (part === '{total}') {
        textRuns.push(PageNumber.TOTAL_PAGES);
      } else if (part) {
        textRuns.push(new TextRun({
          text: part,
          size: 20,
        }));
      }
    });
    
    return textRuns;
  }

  /**
   * Create tabs for page number positioning
   */
  createTabsForPosition(position) {
    const tabs = [];
    
    if (position === 'center') {
      tabs.push({
        pos: 4320, // Center of page (6 inches * 720 twips)
        leader: TabStopType.NONE,
        type: TabStopPosition.CENTER,
      });
    } else if (position === 'right') {
      tabs.push({
        pos: 8640, // Right side (12 inches * 720 twips)
        leader: TabStopType.NONE,
        type: TabStopPosition.RIGHT,
      });
    }
    
    return tabs;
  }

  /**
   * Create enhanced Word styles
   */
  createWordStyles() {
    return {
      paragraphStyles: [
        {
          id: 'Normal',
          name: 'Normal',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: 22,
            font: DEFAULT_EXPORT_FONT_FAMILY,
          },
          paragraph: {
            spacing: {
              after: 120,
              line: 276,
            },
          },
        },
        {
          id: 'Heading1',
          name: 'Heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: HEADING_SIZE_HALF_POINTS_BY_LEVEL[1],
            bold: true,
            color: '1a1a1a',
            font: DEFAULT_EXPORT_FONT_FAMILY,
          },
          paragraph: {
            spacing: {
              before: 480,
              after: 240,
            },
          },
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: HEADING_SIZE_HALF_POINTS_BY_LEVEL[2],
            bold: true,
            color: '1a1a1a',
            font: DEFAULT_EXPORT_FONT_FAMILY,
          },
          paragraph: {
            spacing: {
              before: 360,
              after: 200,
            },
          },
        },
        {
          id: 'Heading3',
          name: 'Heading 3',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: {
            size: HEADING_SIZE_HALF_POINTS_BY_LEVEL[3],
            bold: true,
            color: '1a1a1a',
            font: DEFAULT_EXPORT_FONT_FAMILY,
          },
          paragraph: {
            spacing: {
              before: 280,
              after: 160,
            },
          },
        },
      ],
    };
  }

  createNumberingConfig() {
    return {
      config: [
        {
          reference: 'default-numbering',
          levels: [
            {
              level: 0,
              format: 'decimal',
              text: '%1.',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 },
                },
              },
            },
            {
              level: 1,
              format: 'lowerLetter',
              text: '%2)',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 1440, hanging: 360 },
                },
              },
            },
            {
              level: 2,
              format: 'lowerRoman',
              text: '%3)',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 2160, hanging: 360 },
                },
              },
            },
            {
              level: 3,
              format: 'decimal',
              text: '%4.',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 2880, hanging: 360 },
                },
              },
            },
            {
              level: 4,
              format: 'lowerLetter',
              text: '%5)',
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 3600, hanging: 360 },
                },
              },
            },
          ],
        },
      ],
    };
  }

  /**
   * Wraps fragment HTML in a full document with shared export styles (HTML / print PDF).
   *
   * @param {string} htmlContent - Body HTML from the editor.
   * @param {string} [projectName] - Used for `<title>` when `documentTitle` is omitted.
   * @param {object} [options] - Export options.
   * @param {boolean} [options.includeDocumentHeader] - If true, prepends centered project title + date (default false).
   * @param {string} [options.documentTitle] - Overrides `<title>` and header title when `includeDocumentHeader` is true.
   * @returns {string} Full HTML document string.
   */
  createEnhancedHTML(htmlContent, projectName, options = {}) {
    const {
      includeDocumentHeader = false,
      documentTitle,
    } = options;
    const title = documentTitle || projectName || 'Generated Document';
    const date = new Date().toLocaleDateString();
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <style>
        /* Enhanced typography and layout */
        * {
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
            font-size: 16px;
            line-height: 1.6;
            max-width: 900px;
            margin: 0 auto;
            padding: 40px 20px;
            color: #333;
            background-color: #fff;
        }
        
        .document-header {
            text-align: center;
            margin-bottom: 60px;
            border-bottom: 2px solid #e0e0e0;
            padding-bottom: 30px;
        }
        
        .document-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 10px;
            color: #1a1a1a;
        }
        
        .document-meta {
            font-style: italic;
            color: #666;
            font-size: 1rem;
        }
        
        /* Enhanced typography */
        h1, h2, h3, h4, h5, h6 {
            font-weight: 700;
            margin-top: 2em;
            margin-bottom: 1em;
            line-height: 1.3;
        }
        
        /* Match EnhancedRichTextEditor: h1 2em, h2 1.5em, h3 1.2em @ 16px body */
        h1 {
            font-size: 2rem;
            color: #1a1a1a;
            border-bottom: 3px solid #1976d2;
            padding-bottom: 0.5rem;
        }
        
        h2 {
            font-size: 1.5rem;
            color: #1a1a1a;
        }
        
        h3 {
            font-size: 1.2rem;
            color: #1a1a1a;
        }
        
        p {
            margin-bottom: 1.2em;
            text-align: justify;
            hyphens: auto;
        }
        
        /* Enhanced lists */
        ul, ol {
            margin: 1.2em 0;
            padding-left: 2em;
        }
        
        li {
            margin-bottom: 0.5em;
            line-height: 1.6;
        }
        
        /* Enhanced tables */
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 2em 0;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border-radius: 8px;
            overflow: hidden;
        }
        
        th, td {
            border: 1px solid #e0e0e0;
            padding: 12px 16px;
            text-align: left;
            vertical-align: top;
        }
        
        th {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            font-weight: 600;
            color: #1a1a1a;
        }
        
        tr:nth-child(even) {
            background-color: #f8f9fa;
        }
        
        tr:hover {
            background-color: #e3f2fd;
        }
        
        /* Enhanced blockquotes */
        blockquote {
            border-left: 4px solid #1976d2;
            padding-left: 1.5em;
            margin: 2em 0;
            font-style: italic;
            color: #555;
            background-color: #f8f9fa;
            padding: 1em 1.5em;
            border-radius: 0 8px 8px 0;
        }
        
        /* Enhanced code */
        code {
            background-color: #f1f3f4;
            padding: 0.2em 0.4em;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 0.9em;
            border: 1px solid #e0e0e0;
        }
        
        pre {
            background-color: #f8f9fa;
            padding: 1.5em;
            border-radius: 8px;
            overflow-x: auto;
            border: 1px solid #e0e0e0;
            margin: 1.5em 0;
        }
        
        pre code {
            background: none;
            border: none;
            padding: 0;
        }
        
        /* Enhanced images — align with editor data-align (EXPORT_HTML_CONTRACT) */
        img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            display: block;
            margin-top: 1.5em;
            margin-bottom: 1.5em;
        }
        img[data-align="left"] {
            margin-left: 0;
            margin-right: auto;
        }
        img[data-align="center"],
        img:not([data-align]) {
            margin-left: auto;
            margin-right: auto;
        }
        img[data-align="right"] {
            margin-left: auto;
            margin-right: 0;
        }
        
        /* Enhanced links */
        a {
            color: #1976d2;
            text-decoration: none;
            border-bottom: 1px solid transparent;
            transition: all 0.3s ease;
        }
        
        a:hover {
            border-bottom-color: #1976d2;
            background-color: rgba(25, 118, 210, 0.05);
        }
        
        /* Text formatting */
        strong, b {
            font-weight: 700;
        }
        
        em, i {
            font-style: italic;
        }
        
        u {
            text-decoration: underline;
        }
        
        s, strike, del {
            text-decoration: line-through;
        }
        
        sub {
            vertical-align: sub;
            font-size: 0.8em;
        }
        
        sup {
            vertical-align: super;
            font-size: 0.8em;
        }
        
        /* Highlighting */
        mark {
            background-color: #fff3cd;
            padding: 0.1em 0.2em;
            border-radius: 3px;
        }
        
        /* Print styles */
        @media print {
            body {
                margin: 0;
                padding: 20px;
                font-size: 12pt;
                line-height: 1.4;
            }
            
            .document-header {
                margin-bottom: 30px;
                padding-bottom: 20px;
            }
            
            h1, h2, h3 {
                page-break-after: avoid;
            }
            
            table, img {
                page-break-inside: avoid;
            }
            
            hr {
                visibility: hidden;
                page-break-after: always;
                margin: 0;
                padding: 0;
                border: none;
                height: 0;
            }
            
            a {
                color: #000;
                text-decoration: underline;
            }
        }
        
        /* Responsive design */
        @media (max-width: 768px) {
            body {
                padding: 20px 15px;
            }
            
            .document-title {
                font-size: 2rem;
            }
            
            h1 {
                font-size: 1.75rem;
            }
            
            h2 {
                font-size: 1.35rem;
            }
            
            table {
                font-size: 0.9rem;
            }
            
            th, td {
                padding: 8px 12px;
            }
        }
    </style>
</head>
<body>
    ${includeDocumentHeader && projectName ? `
    <div class="document-header">
        <h1 class="document-title">${projectName}</h1>
        <div class="document-meta">Generated on ${date}</div>
    </div>
    ` : ''}
    
    <div class="document-content">
        ${htmlContent}
    </div>
</body>
</html>`;
  }

  /**
   * Take enhanced HTML and make it print-ready for browser "Save as PDF"
   */
  preparePrintableHTML(enhancedHTML, fileName = 'document.pdf') {
    let htmlForPrint = enhancedHTML;
    const documentTitle = fileName?.replace(/\.pdf$/i, '') || 'Generated Document';
    
    const printSpecificStyles = `
    <style>
      body {
        margin: 0 !important;
        padding: 0 !important;
        max-width: 100% !important;
        background: #fff !important;
      }
      .document-header,
      .document-content {
        margin-left: auto !important;
        margin-right: auto !important;
        max-width: 900px;
        padding-left: 0;
        padding-right: 0;
      }
      .document-header {
        padding-top: 1in;
        padding-left: 40px;
        padding-right: 40px;
      }
      .document-content {
        padding-left: 40px;
        padding-right: 40px;
        padding-bottom: 1in;
      }
      @media print {
        body {
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
        }
        .document-header {
          page-break-after: avoid;
        }
        img, table, pre, blockquote {
          page-break-inside: avoid;
        }
        h1, h2, h3, h4 {
          page-break-after: avoid;
        }
        hr {
          visibility: hidden;
          page-break-after: always;
          margin: 0;
          padding: 0;
          border: none;
          height: 0;
        }
      }
      @page {
        margin: 1in 0;
      }
    </style>
    `;
    
    const printScript = `
    <script>
      document.title = ${JSON.stringify(documentTitle)};
      window.addEventListener('load', () => {
        setTimeout(() => {
          window.focus();
          window.print();
        }, 300);
      });
    </script>
    `;
    
    if (htmlForPrint.includes('</head>')) {
      htmlForPrint = htmlForPrint.replace('</head>', `${printSpecificStyles}</head>`);
    } else {
      htmlForPrint = `${printSpecificStyles}${htmlForPrint}`;
    }
    
    if (htmlForPrint.includes('</body>')) {
      htmlForPrint = htmlForPrint.replace('</body>', `${printScript}</body>`);
    } else {
      htmlForPrint = `${htmlForPrint}${printScript}`;
    }
    
    return htmlForPrint;
  }

  /**
   * Embed images in HTML content
   */
  async embedImagesInHTML(htmlContent) {
    // Use the imageService to embed all images
    return imageService.embedImagesInHtml(htmlContent);
  }

  /**
   * Parse HTML for PDF with formatting
   */
  async parseHTMLForPDF(htmlContent, processedImages) {
    // Implementation similar to Word parsing but for PDF
    // This would create a structure suitable for PDF rendering
    // For now, return a simplified version
    return [
      {
        type: 'html',
        content: htmlContent,
        images: processedImages
      }
    ];
  }

  /**
   * Render enhanced PDF content
   */
  renderEnhancedPDFContent(sections) {
    // Enhanced PDF rendering with proper formatting
    // This is a simplified implementation
    return sections.map((section, index) => {
      if (section.type === 'html') {
        // Parse HTML and create PDF elements
        return (
          <View key={index}>
            <Text style={enhancedPdfStyles.paragraph}>
              {section.content.replace(/<[^>]*>/g, '')} {/* Strip HTML for now */}
            </Text>
          </View>
        );
      }
      return null;
    }).filter(Boolean);
  }
}

const enhancedExportService = new EnhancedExportService();
export default enhancedExportService;
